import {
  computed,
  getCurrentScope,
  onScopeDispose,
  ref,
  shallowRef,
} from 'vue';
import type {
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
} from 'pdfjs-dist/legacy/build/pdf.mjs';

type PdfContext = {
  canvas: HTMLCanvasElement;
  document: PDFDocumentProxy;
};

const loadPDFJS = async () => {
  const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');

  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
      import.meta.url,
    ).toString();
  }

  return pdfjsLib;
};

export const usePdfRenderer = () => {
  const isLoading = ref(true);
  const error = ref<string | null>(null);
  const pdfContext = shallowRef<PdfContext | null>(null);
  const currentPage = ref(1);
  const totalPages = computed(() => pdfContext.value?.document.numPages ?? 0);

  const zoomMultiplier = ref(1);

  const performRender = async () => {
    const pdf = pdfContext.value;
    if (!pdf) {
      return;
    }

    try {
      const page = await pdf.document.getPage(currentPage.value);

      const { canvas } = pdf;
      const container = canvas.parentElement;
      const containerWidth = container?.clientWidth || 0;
      const viewportWidth =
        (globalThis.window !== undefined && window.innerWidth) ||
        (typeof document !== 'undefined' &&
          document.documentElement.clientWidth) ||
        600;
      const baseWidth =
        containerWidth > 0 ? containerWidth : Math.max(viewportWidth - 32, 320);
      const effectiveWidth = Math.max(baseWidth - 40, 200);
      const maxWidth = Math.min(effectiveWidth, 800);

      const viewport = page.getViewport({ scale: 1 });

      const targetScale = (maxWidth / viewport.width) * zoomMultiplier.value;
      const scaledViewport = page.getViewport({ scale: targetScale });

      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      canvas.style.width = `${scaledViewport.width}px`;
      canvas.style.height = `${scaledViewport.height}px`;

      const renderTask = page.render({
        canvas,
        viewport: scaledViewport,
      });
      await renderTask.promise;
      error.value = null;
    } catch (err: unknown) {
      if (pdf !== pdfContext.value) {
        return;
      }
      // eslint-disable-next-line no-console
      console.error(err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      error.value = `Failed to render page ${currentPage.value}: ${errorMessage}`;
    }
  };

  // pdf.js forbids overlapping render() calls on one canvas; rapid page or
  // zoom clicks would otherwise throw "Cannot use the same canvas during
  // multiple render() operations". Renders are serialized through a queue,
  // and requests superseded by a newer one are skipped.
  let renderQueue: Promise<void> = Promise.resolve();
  let renderVersion = 0;

  const renderCurrentPage = (): Promise<void> => {
    renderVersion += 1;
    const version = renderVersion;
    renderQueue = renderQueue
      .then(() => {
        if (version !== renderVersion) return;
        return performRender();
      })
      .catch(() => {
        /* errors are surfaced via the error ref */
      });
    return renderQueue;
  };

  let loadingTask: PDFDocumentLoadingTask | null = null;
  let loadVersion = 0;

  const releaseDocument = () => {
    loadVersion += 1;
    loadingTask?.destroy();
    loadingTask = null;
    pdfContext.value = null;
  };

  if (getCurrentScope()) {
    onScopeDispose(releaseDocument);
  }

  const loadPDF = async (canvas: HTMLCanvasElement, fileUrl: string) => {
    releaseDocument();
    const version = loadVersion;

    try {
      isLoading.value = true;
      error.value = null;

      const pdfjsLib = await loadPDFJS();
      if (version !== loadVersion) return;

      loadingTask = pdfjsLib.getDocument({
        url: fileUrl,
        disableAutoFetch: true,
        disableStream: true,
        disableRange: true,
      });

      const pdfDocument = await loadingTask.promise;
      if (version !== loadVersion) return;

      pdfContext.value = { canvas, document: pdfDocument };
      currentPage.value = 1;

      await renderCurrentPage();
    } catch (err: unknown) {
      if (version !== loadVersion) return;
      // eslint-disable-next-line no-console
      console.error(err);
      error.value = 'Failed to load PDF. Please try again.';
    } finally {
      if (version === loadVersion) {
        isLoading.value = false;
      }
    }
  };

  const nextPage = async () => {
    if (currentPage.value < totalPages.value) {
      currentPage.value++;
      await renderCurrentPage();
    }
  };

  const previousPage = async () => {
    if (currentPage.value > 1) {
      currentPage.value--;
      await renderCurrentPage();
    }
  };

  const goToPage = async (page: number) => {
    const clamped = Math.min(
      Math.max(Math.trunc(page) || 1, 1),
      totalPages.value || 1,
    );
    if (clamped === currentPage.value) return;
    currentPage.value = clamped;
    await renderCurrentPage();
  };

  const zoomIn = async () => {
    if (zoomMultiplier.value < 3) {
      zoomMultiplier.value = Math.min(zoomMultiplier.value + 0.25, 3);
      await renderCurrentPage();
    }
  };

  const zoomOut = async () => {
    if (zoomMultiplier.value > 0.5) {
      zoomMultiplier.value = Math.max(zoomMultiplier.value - 0.25, 0.5);
      await renderCurrentPage();
    }
  };

  return {
    isLoading,
    error,
    currentPage,
    totalPages,
    zoomMultiplier,

    loadPDF,
    renderCurrentPage,
    nextPage,
    previousPage,
    goToPage,
    zoomIn,
    zoomOut,
  };
};
